/**
 * ASCII Art Generator - 呼吸效果计算
 *
 * 局部呼吸动画：不同区域有相位差，影响亮度/透明度/大小
 */

import type { BreatheConfig } from '../animation/types'

/**
 * 计算呼吸效果
 *
 * @param charX 字符X位置
 * @param charY 字符Y位置
 * @param time 当前时间
 * @param config 呼吸配置
 * @returns 呼吸效果结果
 */
export function calculateBreatheEffect(
  charX: number,
  charY: number,
  time: number,
  config: BreatheConfig
): { brightnessFactor: number; opacityFactor: number; scale: number } {
  const {
    amplitude,
    frequency,
    phaseSpread,
    affectBrightness,
    affectOpacity,
    affectScale
  } = config

  // 基于位置计算相位差，创建局部呼吸效果
  // 使用字符位置生成不同的相位
  const phaseX = charX * phaseSpread
  const phaseY = charY * phaseSpread

  // 组合成正弦波
  const wave = Math.sin(time * frequency * Math.PI * 2 + phaseX + phaseY)

  // 计算各因子
  const brightnessFactor = affectBrightness
    ? 1 + wave * amplitude
    : 1

  const opacityFactor = affectOpacity
    ? 0.3 + (wave + 1) * 0.35 * amplitude // 范围约 0.3-1.0
    : 1

  const scale = affectScale
    ? 1 + wave * amplitude * 0.2
    : 1

  return { brightnessFactor, opacityFactor, scale }
}

/**
 * 呼吸效果类
 */
export class BreatheEffect {
  private config: BreatheConfig
  private time = 0

  constructor(config: Partial<BreatheConfig> = {}) {
    this.config = {
      amplitude: 0.3,
      frequency: 0.5,
      phaseSpread: 0.01,
      affectBrightness: true,
      affectOpacity: true,
      affectScale: false,
      ...config
    }
  }

  /**
   * 更新时间
   */
  update(deltaTime: number): void {
    this.time += deltaTime
  }

  /**
   * 计算指定位置的呼吸效果
   */
  calculate(charX: number, charY: number): {
    brightnessFactor: number
    opacityFactor: number
    scale: number
  } {
    return calculateBreatheEffect(charX, charY, this.time, this.config)
  }

  /**
   * 更新配置
   */
  setConfig(config: Partial<BreatheConfig>): void {
    this.config = { ...this.config, ...config }
  }

  /**
   * 获取当前配置
   */
  getConfig(): BreatheConfig {
    return { ...this.config }
  }

  /**
   * 重置时间
   */
  reset(): void {
    this.time = 0
  }
}

/**
 * 从设置创建呼吸配置
 */
export function createBreatheConfigFromSettings(
  amplitude: number = 0.3,
  frequency: number = 0.5,
  phaseSpread: number = 0.01
): BreatheConfig {
  return {
    amplitude,
    frequency,
    phaseSpread,
    affectBrightness: true,
    affectOpacity: true,
    affectScale: false
  }
}